/**
 * Modelos del contrato de flotas de taxi/VTC (docs/flotas-drivers/api-contract.md),
 * en camelCase: los servicios convierten desde el snake_case del API.
 */
import {
  AttentionKind,
  BillingEntityType,
  DriverPayoutStatus,
  DriverPresence,
  RepresentativeRole,
  SettlementPeriod,
  VehiclePresence,
  ChallengeParticipantStatus,
  ChallengeStatus,
  DemandChannel,
  DispatchStrategy,
  FeeSimulationReason,
  FleetKind,
  ServiceType,
  DocumentOwnerType,
  DocumentStatus,
  DocumentType,
  DocumentUrgency,
  EmploymentType,
  ExpiryStatus,
  FleetMemberStatus,
  FleetMembershipStatus,
  FleetMode,
  FleetOnboardingStepKey,
  FleetRole,
  FleetStatus,
  FleetVehicleStatus,
  ProfessionalDriverOnboardingStatus,
  ProfessionalDriverWorkingAs,
  RejectionReason,
  SettlementMovementType,
  ShiftSlot,
  Weekday,
} from './drivers-fleet.enums';

// ------------------------------------------------------------------ comunes

export interface FleetPageMeta {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
  from: number | null;
  to: number | null;
  /** Contadores por estado para las pestañas, cuando el endpoint los da. */
  counts?: Record<string, number>;
}

export interface FleetPage<T> {
  data: T[];
  meta: FleetPageMeta;
}

export interface FleetPageQuery {
  page?: number;
  perPage?: number;
}

export interface NamedRef {
  id: string;
  name: string;
}

// ------------------------------------------------------------------ documentos

export interface FleetDocumentFile {
  url: string;
  urlExpiresAt: string | null;
  mimeType: string;
  sizeBytes: number;
  originalName: string;
}

export interface FleetDocument {
  id: string;
  ownerType: DocumentOwnerType;
  ownerId: string;
  type: DocumentType;
  status: DocumentStatus;
  expiresAt: string | null;
  expiryStatus: ExpiryStatus;
  licenseNumber: string | null;
  rejectionReason: RejectionReason | null;
  rejectionComment: string | null;
  /** `null` si no se ha subido o si quien lo lee no puede ver el fichero. */
  file: FleetDocumentFile | null;
  uploadedAt: string | null;
  uploadedBy: { id: string; name: string; kind: 'fleet' | 'driver' } | null;
  reviewedAt: string | null;
  reviewedBy: { id?: string; name: string } | null;
  version: number;
}

export interface DocumentTypeInfo {
  type: DocumentType;
  ownerType: DocumentOwnerType;
  modes: FleetMode[];
  hasExpiry: boolean;
  requiresLicenseNumber: boolean;
}

// ------------------------------------------------------------------ catálogo

export interface LegalText {
  version: string;
  body: string;
}

export interface FleetCatalogs {
  regions: { id: string; name: string; shortName: string | null }[];
  waiisTaxId: string;
  demandChannels: { channel: DemandChannel; feeApplies: boolean }[];
  serviceTypes: ServiceType[];
  legalTexts: {
    fleetTerms: LegalText;
    selfBillingAgreement: LegalText;
    /** Descargo del paso fiscal (mockup 28): una lista de puntos por modo. */
    billingDisclaimer?: { version: string; items: Record<FleetMode, string[]> };
  };
  documentTypes: DocumentTypeInfo[];
  rejectionReasons: RejectionReason[];
  vatRegimes: string[];
}

// ------------------------------------------------------------------ flota

export interface FleetAddress {
  line: string;
  postalCode: string;
  city: string;
  province: string;
  country: string;
}

export interface FleetBilling {
  entityType: BillingEntityType;
  legalName: string;
  taxId: string;
  address: FleetAddress;
  vatRegime: string;
  representative?: { role: RepresentativeRole; name: string | null } | null;
  /** Socios con más del 25 % (si el representante no es administrador único). */
  beneficialOwners?: { name: string; idNumber: string }[];
  expectedFleetSize?: { vehicles: number | null; drivers: number | null } | null;
  /** Versión del descargo aceptado (catálogo `legalTexts.billingDisclaimer`). */
  disclaimerVersion?: string | null;
}

export interface FleetRules {
  expiryReminders: boolean;
  blockOnExpiry: boolean;
  acceptCash: boolean;
  cancellationAlert: boolean;
}

export interface FleetOnboardingStep {
  key: FleetOnboardingStepKey;
  status: DocumentStatus;
  documentId?: string | null;
  rejectionReason: RejectionReason | null;
  rejectionComment: string | null;
  updatedAt: string | null;
}

export interface Fleet {
  id: string;
  name: string;
  kind: FleetKind;
  mode: FleetMode;
  status: FleetStatus;
  region: NamedRef | null;
  contactEmail: string;
  fleetCode: string;
  /** No llega en el listado de admin. */
  billing?: FleetBilling | null;
  bankAccount?: { ibanMasked: string | null } | null;
  rules?: FleetRules;
  legalAcceptance?: {
    fleetTermsVersion: string;
    selfBillingAgreementVersion: string;
    acceptedAt: string;
  } | null;
  onboardingSteps: FleetOnboardingStep[];
  gettingStarted: { hasVehicle: boolean; hasAcceptedDriver: boolean; hasShifts: boolean };
  counts: { vehicles: number; driversAccepted: number; driverRequestsPending: number; documentAlerts?: number };
  submittedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
}

export interface CreateFleetRequest {
  kind: FleetKind;
  regionId: string;
  contactEmail: string;
  name: string;
}

/** Sin cuerpo desde el mockup 28; las versiones de las fases 0-3 se aceptan y se ignoran. */
export interface SubmitFleetRequest {
  fleetTermsVersion?: string;
  selfBillingAgreementVersion?: string;
}

export interface FleetOverviewWeek {
  from: string;
  to: string;
  trips: number;
  fareCents: number;
  driverShareCents: number;
  netCents: number;
  /** Cuota de los pasajeros: solo información, no resta. */
  feeCents: number;
  payoutOn: string | null;
}

export interface AttentionItem {
  kind: AttentionKind;
  label: string;
  detail: string | null;
  target: { type: 'driver' | 'vehicle' | 'fleet'; id: string };
}

export interface FleetOverview {
  /** Mockup 28: la semana de liquidación en curso. */
  week?: FleetOverviewWeek;
  attention?: AttentionItem[];
  fleet?: {
    vehiclesByStatus: Record<FleetVehicleStatus, number>;
    drivers: { ready: number; verifying: number; pending: number };
  };
  /** Fases 0-3 (indicadores con periodo); la portada del mockup 28 ya no los usa. */
  kpis?: {
    driversActive: number;
    vehiclesWorking: number;
    tripsCompleted: number;
    fareTotalCents: number;
    feeTotalCents: number;
  };
  previous?: { tripsCompleted: number; fareTotalCents: number };
  tripsByDay: { date: string; trips: number }[];
  topDrivers: { membershipId: string; name: string; trips: number; fareCents: number; hoursOnline: number }[];
  alerts: {
    documentsExpired: number;
    documentsExpiringSoon: number;
    documentsRejected: number;
    driverRequestsPending: number;
    driversWithoutVehicle?: number;
  };
}

// ------------------------------------------------------------------ conductores

export interface FleetDriverUser {
  id: string;
  name: string;
  surname: string;
  email: string;
  phone: string | null;
  selfieUrl: string | null;
}

export interface DocumentsSummary {
  approved: number;
  inReview: number;
  rejected: number;
  pending: number;
  expired: number;
  expiringSoon: number;
}

export interface DriverWeekStats {
  trips: number;
  hoursOnline: number;
  fareCents: number;
  rating: number | null;
  acceptanceRate: number | null;
  cancellationRate: number | null;
}

export interface FleetDriver {
  membershipId: string;
  user: FleetDriverUser;
  status: FleetMembershipStatus;
  employmentType: EmploymentType | null;
  /** Lo que se queda el conductor; `null` en asalariados. */
  revenueSharePercentage: number | null;
  documentsSummary: DocumentsSummary;
  canWork: boolean;
  /** `false`: desactivado por la flota. */
  active: boolean;
  /** El titular que también conduce («Tú»). */
  isOwner: boolean;
  presence: DriverPresence | null;
  vehicle: { id: string; plate: string } | null;
  shiftLabel: string | null;
  weekStats: DriverWeekStats | null;
  requestedAt: string;
  acceptedAt: string | null;
}

export interface FleetDriverDetail extends FleetDriver {
  /** Sin fichero: la flota solo ve el estado. */
  documents: FleetDocument[];
  shifts: ShiftAssignment[];
}

export interface FleetDriversQuery extends FleetPageQuery {
  status?: FleetMembershipStatus | '';
  search?: string;
  canWork?: boolean | null;
  presence?: DriverPresence | '';
  withAlerts?: boolean | null;
}

export interface AcceptDriverRequest {
  employmentType: EmploymentType;
  revenueSharePercentage?: number | null;
}

// ------------------------------------------------------------------ vehículos

export interface FleetVehicle {
  id: string;
  plate: string;
  /** Licencia municipal (taxi) o nº de autorización (VTC). */
  licenseNumber: string;
  brand: string | null;
  model: string | null;
  year: number | null;
  seats: number | null;
  odometerKm: number | null;
  /** Municipio de la licencia (solo taxi). */
  municipality: string | null;
  status: FleetVehicleStatus;
  canWork: boolean;
  presence: VehiclePresence | null;
  /** Conductores de sus turnos, con la franja y los días en texto. */
  drivers: { membershipId: string; name: string; shiftLabel: string }[];
  documents: FleetDocument[];
  /** `null`: sin enviar a revisión («Guardar y seguir luego»). */
  submittedAt: string | null;
  createdAt: string;
}

export interface FleetVehicleRequest {
  plate?: string;
  licenseNumber: string;
  municipality?: string | null;
  brand?: string | null;
  model?: string | null;
  year?: number | null;
  seats?: number | null;
  odometerKm?: number | null;
}

export interface FleetVehiclesQuery extends FleetPageQuery {
  status?: FleetVehicleStatus | '';
  search?: string;
  canWork?: boolean | null;
  presence?: VehiclePresence | '';
  withoutDriver?: boolean | null;
  withAlerts?: boolean | null;
}

/** Contadores extra de los listados de conductores y vehículos (pestañas del mockup 28). */
export interface PresenceListMeta {
  presenceCounts?: Record<string, number>;
  withAlerts?: number;
  withoutDriver?: number;
}

export interface FleetPresence {
  counts: Record<DriverPresence, number>;
  drivers: {
    membershipId: string;
    name: string;
    presence: DriverPresence;
    plate: string | null;
    location: { lat: number; lng: number } | null;
    updatedAt: string;
  }[];
  /** Centro de la región, para el mapa vacío. */
  center: { lat: number; lng: number };
  updatedAt: string;
}

// ------------------------------------------------------------------ turnos

export interface ShiftSlotHours {
  slot: ShiftSlot;
  /** `HH:mm` en hora local de la flota. */
  start: string;
  end: string;
}

export interface ShiftAssignment {
  vehicleId: string;
  weekday: Weekday;
  slot: ShiftSlot;
  membershipId: string;
}

export interface FleetShifts {
  assignments: ShiftAssignment[];
  updatedAt: string | null;
  /** Al guardar: desde cuándo se aplican los cambios (mañana). */
  effectiveFrom?: string | null;
}

export type ShiftConflict =
  | {
      kind: 'driver_double_booked';
      weekday: Weekday;
      slot: ShiftSlot;
      membershipId: string;
      vehicleIds: string[];
    }
  | {
      kind: 'slot_taken';
      weekday: Weekday;
      slot: ShiftSlot;
      vehicleId: string;
      membershipIds: string[];
    };

// ------------------------------------------------------------------ documentos de la flota

export interface DocumentsMatrixRow {
  vehicleId: string;
  plate: string;
  /** Marca y modelo («Toyota Corolla»), si se conocen. */
  vehicleLabel: string | null;
  urgency: DocumentUrgency;
  /** Un documento por columna (el servicio convierte el mapa por tipo del API). */
  documents: FleetDocument[];
}

export interface DocumentsMatrix {
  columns: DocumentType[];
  company: { documents: FleetDocument[] };
  vehicles: DocumentsMatrixRow[];
  upcomingExpirations: {
    ownerType: DocumentOwnerType;
    ownerId: string;
    ownerLabel: string;
    type: DocumentType;
    expiresAt: string;
  }[];
}

// ------------------------------------------------------------------ liquidaciones

export interface SettlementsSummary {
  balanceCents: number;
  pendingCents: number;
  nextPayoutAt: string | null;
  defaultRevenueSharePercentage: number;
  payoutsEnabled: boolean;
}

/** Desglose de un viaje: la flota cobra `fareCents`; la cuota la paga el pasajero encima. */
export interface TripFareBreakdown {
  id: string;
  fareCents: number;
  demandChannel: DemandChannel;
  serviceType: ServiceType;
  feeCents: number;
  passengerTotalCents: number;
}

export interface SettlementStatement {
  period: SettlementPeriod;
  from: string;
  to: string;
  openingBalanceCents: number;
  lines: {
    trips: { count: number; amountCents: number };
    tolls: { amountCents: number };
    driverShare: { amountCents: number };
    cancellations: { count: number; amountCents: number };
    adjustments: { amountCents: number };
    /** Lo transferido a la cuenta de la flota (negativo). */
    payouts: { amountCents: number };
  };
  /** Cuota de los pasajeros del periodo: solo información. */
  feeCents: number;
  closingBalanceCents: number;
  availableOn: string | null;
  heldCents: number;
  drivers: {
    membershipId: string;
    name: string;
    generatedCents: number;
    sharePercentage: number | null;
    payableCents: number;
    status: DriverPayoutStatus;
    paidAt: string | null;
  }[];
}

export interface SettlementMovement {
  id: string;
  occurredAt: string;
  type: SettlementMovementType;
  amountCents: number;
  trip: TripFareBreakdown | null;
  driver: { membershipId: string; name: string } | null;
  description: string | null;
}

export interface SettlementMovementsQuery extends FleetPageQuery {
  from?: string;
  to?: string;
  type?: SettlementMovementType | '';
  membershipId?: string;
}

// ------------------------------------------------------------------ rendimiento

export interface DriverPerformance {
  membershipId: string;
  name: string;
  trips: number;
  hoursOnline: number;
  fareTotalCents: number;
  fareCentsPerHour: number;
  acceptanceRate: number;
  cancellationRate: number;
  rating: number | null;
}

export interface DriverPerformanceQuery extends FleetPageQuery {
  from?: string;
  to?: string;
  sort?: 'fare_per_hour' | 'rating' | 'acceptance_rate' | 'trips' | 'fare_total' | 'hours_online';
  order?: 'asc' | 'desc';
}

export interface PerformanceSummary {
  ratingAvg: number | null;
  acceptanceRate: number | null;
  cancellationRate: number | null;
  fareCentsPerHour: number | null;
}

export interface DemandByHour {
  weeks: number;
  hours: { hour: number; trips: number; vehiclesParked: number }[];
}

export interface MonthlyReport {
  /** `YYYY-MM`. */
  month: string;
  closed: boolean;
  closedAt: string | null;
  trips: number;
  fareCents: number;
  driverShareCents: number;
  netCents: number;
  feeCents: number;
  driversPaid: number;
}

export interface AccountingSettings {
  monthlyEmailEnabled: boolean;
  email: string | null;
}

// ------------------------------------------------------------------ equipo y ajustes

export interface FleetMember {
  id: string;
  email: string;
  name: string | null;
  lastSeenAt: string | null;
  user: { id: string; name: string } | null;
  role: FleetRole;
  status: FleetMemberStatus;
  invitedAt: string;
}

export interface FleetNotificationSettings {
  documentExpiring: boolean;
  documentRejected: boolean;
  driverRequests: boolean;
  weeklySettlement: boolean;
}

// ------------------------------------------------------------------ admin: verificación

export interface DocumentReviewItem {
  document: FleetDocument;
  mode: FleetMode;
  owner: { type: DocumentOwnerType; id: string; label: string };
  fleet: NamedRef | null;
  driver: NamedRef | null;
}

export interface DocumentReviewHistoryEntry {
  action: 'uploaded' | 'approved' | 'rejected';
  actor: NamedRef | null;
  at: string;
  reason: RejectionReason | null;
  comment: string | null;
}

export interface DocumentReviewDetail extends Omit<DocumentReviewItem, 'driver'> {
  driver:
    | (NamedRef & {
        identityCheck: { status: DocumentStatus; checkedAt: string | null; provider: string } | null;
      })
    | null;
  previousVersions: {
    id: string;
    version: number;
    status: DocumentStatus;
    rejectionReason: RejectionReason | null;
    uploadedAt: string;
  }[];
  history: DocumentReviewHistoryEntry[];
}

export interface DocumentReviewsQuery extends FleetPageQuery {
  status?: DocumentStatus | '';
  ownerType?: DocumentOwnerType | '';
  type?: DocumentType | '';
  mode?: FleetMode | '';
  search?: string;
  sort?: 'uploaded_at' | '-uploaded_at';
}

export interface ApproveDocumentRequest {
  expiresAt?: string | null;
  licenseNumber?: string | null;
}

export interface RejectDocumentRequest {
  reason: RejectionReason;
  comment?: string | null;
}

// ------------------------------------------------------------------ admin: flotas y conductores

export interface AdminFleetsQuery extends FleetPageQuery {
  status?: FleetStatus | '';
  mode?: FleetMode | '';
  regionId?: string;
  search?: string;
}

export interface ProfessionalDriver {
  user: FleetDriverUser;
  mode: FleetMode;
  workingAs: ProfessionalDriverWorkingAs;
  region: NamedRef | null;
  onboardingStatus: ProfessionalDriverOnboardingStatus;
  steps: { key: DocumentType | 'billing_info'; status: DocumentStatus }[];
  fleet: NamedRef | null;
  canWork: boolean;
  createdAt: string;
}

export interface ProfessionalDriverDetail extends ProfessionalDriver {
  documents: FleetDocument[];
  billing: FleetBilling | null;
  fleets: (NamedRef & { status: FleetMembershipStatus; since: string; until: string | null })[];
}

export interface ProfessionalDriversQuery extends FleetPageQuery {
  mode?: FleetMode | '';
  onboardingStatus?: ProfessionalDriverOnboardingStatus | '';
  workingAs?: ProfessionalDriverWorkingAs | '';
  search?: string;
}

// ------------------------------------------------------------------ emisora: paradas, zonas y reparto

/** Punto `[lat, lng]`; un polígono es una lista sin cerrar (mínimo 3). */
export type GeoPoint = [number, number];

export interface TaxiStand {
  id: string;
  name: string;
  address: string | null;
  location: { lat: number; lng: number };
  geofence: GeoPoint[];
  capacity: number | null;
  active: boolean;
  queueCount: number;
  updatedAt: string;
}

export interface TaxiStandRequest {
  name: string;
  address?: string | null;
  location: { lat: number; lng: number };
  geofence: GeoPoint[];
  capacity?: number | null;
  active?: boolean;
}

export interface StandQueueEntry {
  position: number;
  membershipId: string;
  name: string;
  plate: string | null;
  joinedAt: string;
  consecutiveRejections: number;
  skippedByRequirements: number;
}

export interface StandQueue {
  entries: StandQueueEntry[];
  updatedAt: string;
}

export interface FleetZone {
  id: string;
  name: string;
  polygon: GeoPoint[];
  active: boolean;
  updatedAt: string;
}

export interface FleetZoneRequest {
  name: string;
  polygon: GeoPoint[];
  active?: boolean;
}

export interface DispatchPolicy {
  strategy: DispatchStrategy;
  intraFleetPriority: boolean;
  offerTimeoutSeconds: number;
  standMaxConsecutiveRejections: number;
  skipByRequirementsKeepsPosition: boolean;
  updatedAt: string | null;
  updatedBy: NamedRef | null;
}

export interface AuditChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface AuditEntry {
  id: string;
  at: string;
  actor: NamedRef | null;
  /** Solo en la auditoría de la cuota. */
  action?: 'rule_created' | 'rule_updated' | 'rule_deleted' | 'region_toggled';
  target?: string | null;
  changes: AuditChange[];
}

// ------------------------------------------------------------------ admin: cuota al pasajero

export interface PassengerFeeRule {
  id: string;
  region: NamedRef | null;
  mode: FleetMode | null;
  serviceType: ServiceType;
  amountCents: number;
  updatedAt: string;
}

export interface PassengerFeeRuleRequest {
  regionId: string | null;
  mode: FleetMode | null;
  serviceType: ServiceType;
  amountCents: number;
}

export interface PassengerFeeRegion {
  region: NamedRef;
  feesEnabled: boolean;
  changedAt: string | null;
  changedBy: { name: string } | null;
  reason: string | null;
}

export interface PassengerFeesMatrix {
  rules: PassengerFeeRule[];
  regions: PassengerFeeRegion[];
}

export interface PassengerFeeSimulationRequest {
  regionId: string;
  mode: FleetMode;
  demandChannel: DemandChannel;
  serviceType: ServiceType;
  fareCents: number;
}

export interface PassengerFeeSimulation {
  feeCents: number;
  passengerTotalCents: number;
  ruleId: string | null;
  reason: FeeSimulationReason;
}

// ------------------------------------------------------------------ admin: retos

export interface ChallengeGoals {
  minTrips: number | null;
  minHoursOnline: number | null;
  minFareCents: number | null;
  minRating: number | null;
}

export interface DriverChallenge {
  id: string;
  title: string;
  description: string | null;
  region: NamedRef | null;
  mode: FleetMode | null;
  startsAt: string;
  endsAt: string;
  goals: ChallengeGoals;
  bonusCents: number;
  status: ChallengeStatus;
  counts: { participants: number; completed: number };
  bonusPaidCents: number;
  createdAt: string;
}

export interface DriverChallengeRequest {
  title: string;
  description?: string | null;
  regionId: string | null;
  mode: FleetMode | null;
  startsAt: string;
  endsAt: string;
  goals: ChallengeGoals;
  bonusCents: number;
}

export interface DriverChallengesQuery extends FleetPageQuery {
  status?: ChallengeStatus | '';
  regionId?: string;
  search?: string;
}

export interface ChallengeParticipant {
  user: NamedRef;
  progress: { trips: number; hoursOnline: number; fareCents: number; rating: number | null };
  status: ChallengeParticipantStatus;
  completedAt: string | null;
  bonusPaidAt: string | null;
}

// ------------------------------------------------------------------ errores de negocio

/** `error_type` de los 409/422 del contrato. */
export type FleetApiErrorType =
  | 'fleet_onboarding_incomplete'
  | 'legal_texts_outdated'
  | 'membership_not_pending'
  | 'vehicle_plate_taken'
  | 'shifts_locked'
  | 'shifts_outdated'
  | 'shift_conflict'
  | 'member_already_exists'
  | 'last_owner'
  | 'document_already_reviewed'
  | 'billing_not_in_review'
  | 'not_a_dispatch_center'
  | 'dispatch_policy_outdated'
  | 'fee_rule_duplicate'
  | 'fee_rule_required'
  | 'challenge_locked'
  | 'vehicle_documents_missing'
  | 'vehicle_already_submitted'
  | 'member_already_active';
